import type { ReactElement, ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { BlockedState } from "@swot/base-components";
import type { PermissionContext } from "@swot/swot-components";
import { buildPermissionContext } from "@cmts/auth/permissionBridge.js";
import { readCmtsSession } from "@cmts/auth/authStore.js";
import { useCmtsDispatch, useCmtsSelector } from "@store/hooks.js";
import { permissionActions } from "@store/permissionSlice.js";
import { selectAuthState, selectEffectivePermissions, selectRoles } from "@store/selectors.js";
import { CmtsRouteLoadingFallback } from "./CmtsRouteLoadingFallback.js";

export type CmtsRouteAccessRequirement = {
  readonly public?: boolean | undefined;
  readonly requiresAuth?: boolean | undefined;
  readonly requiredPermission?: string | undefined;
  readonly requiredActionCode?: string | undefined;
  readonly requiredAnyPermission?: readonly string[] | undefined;
  readonly requiredAllPermissions?: readonly string[] | undefined;
};

function hasRequiredPermission(permissions: readonly string[], access: CmtsRouteAccessRequirement): boolean {
  if (access.public || access.requiredPermission === "PUBLIC") return true;
  const upperCasePermissions = permissions.map((p) => p.trim().toUpperCase());
  if (access.requiredPermission) {
    const required = access.requiredPermission.trim().toUpperCase();
    if (!upperCasePermissions.includes(required)) return false;
  }
  if (access.requiredAnyPermission?.length) {
    const hasAny = access.requiredAnyPermission.some((p) => upperCasePermissions.includes(p.trim().toUpperCase()));
    if (!hasAny) return false;
  }
  if (access.requiredAllPermissions?.length) {
    const hasAll = access.requiredAllPermissions.every((p) => upperCasePermissions.includes(p.trim().toUpperCase()));
    if (!hasAll) return false;
  }
  return true;
}

export function useCmtsRoutePermission(access: CmtsRouteAccessRequirement): {
  readonly allowed: boolean;
  readonly authenticated: boolean;
  readonly loading: boolean;
  readonly failed: boolean;
  readonly permissions: readonly string[];
  readonly roles: readonly string[];
  readonly permissionContext: PermissionContext | null;
} {
  const auth = useCmtsSelector(selectAuthState);
  const permissions = useCmtsSelector(selectEffectivePermissions);
  const roles = useCmtsSelector(selectRoles);
  const storedSession = readCmtsSession();
  const storedHasSession = Boolean(storedSession?.accessToken);
  const authenticated = auth.bootstrapStatus === "authenticated" && auth.status === "authenticated" && Boolean(auth.session?.accessTokenPresent);
  const loading = !access.public && (auth.bootstrapStatus === "restoring" || (auth.bootstrapStatus === "idle" && storedHasSession) || auth.status === "loading" || auth.status === "refreshing");
  const failed = !access.public && auth.bootstrapStatus === "failed";
  const uniquePermissions = useMemo(() => authenticated ? Array.from(new Set(permissions.map(String).filter(Boolean))).sort() : [], [authenticated, permissions.join("|")]);
  const uniqueRoles = useMemo(() => authenticated ? Array.from(new Set(roles.map(String).filter(Boolean))).sort() : [], [authenticated, roles.join("|")]);
  const permissionContext = useMemo<PermissionContext | null>(() => {
    if (!authenticated && !access.public) return null;
    return buildPermissionContext({
      userId: auth.session?.userId,
      tenantId: auth.session?.tenantId,
      roles: uniqueRoles,
      permissions: uniquePermissions,
      siteIds: auth.session?.siteIds,
      confidentialityClearance: auth.session?.confidentialityClearance?.toLowerCase?.() as PermissionContext["confidentialityClearance"] | undefined,
    });
  }, [access.public, auth.session, authenticated, uniquePermissions, uniqueRoles]);
  return { allowed: authenticated ? hasRequiredPermission(uniquePermissions, access) : Boolean(access.public), authenticated, loading, failed, permissions: uniquePermissions, roles: uniqueRoles, permissionContext };
}

export function CmtsProtectedRoute(props: { readonly access: CmtsRouteAccessRequirement; readonly routePath: string; readonly children: ReactNode }): ReactElement {
  const location = useLocation();
  const dispatch = useCmtsDispatch();
  const authState = useCmtsSelector(selectAuthState);
  const routeState = useCmtsRoutePermission(props.access);
  const shouldBlock = !props.access.public && routeState.authenticated && !routeState.loading && !routeState.allowed;

  useEffect(() => {
    if (!shouldBlock) return;
    dispatch(permissionActions.permissionBlocked({
      permission: props.access.requiredPermission,
      route: props.routePath,
      message: `Missing required permission or action for ${props.routePath}.`,
    }));
  }, [dispatch, props.access.requiredPermission, props.routePath, shouldBlock]);

  if (props.access.public) return <>{props.children}</>;

  if (routeState.loading) return <CmtsRouteLoadingFallback label="Checking CMTS route permissions" />;

  if (routeState.failed) {
    return <BlockedState title="CMTS session restore failed" message={authState.error?.message ?? "The protected route cannot be opened until the CMTS session is restored."} />;
  }

  if (!routeState.authenticated) {
    return <Navigate to="/cmts/login" replace state={{ returnTo: location.pathname }} />;
  }

  if (shouldBlock) {
    return <Navigate to="/cmts/access-denied" replace state={{ requiredPermission: props.access.requiredPermission, requiredActionCode: props.access.requiredActionCode, attemptedPath: location.pathname }} />;
  }

  return <>{props.children}</>;
}
